import { useEffect, useState } from "react";
import type { GalleryImage } from "@/mocks/profile";

export default function Gallery({ images }: { images: GalleryImage[] }) {
  const [active, setActive] = useState<number | null>(null);

  const close = () => setActive(null);
  const prev = () => setActive((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  const next = () => setActive((i) => (i === null ? null : (i + 1) % images.length));

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, images.length]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        {images.map((img, i) => (
          <button
            key={img.caption}
            type="button"
            onClick={() => setActive(i)}
            className="group relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200 cursor-pointer"
          >
            <img
              src={img.src}
              alt={img.caption}
              title={`${img.caption} - 森泽佳奈精彩瞬间`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <span className="absolute inset-x-0 bottom-0 px-4 py-3 bg-gradient-to-t from-primary-950/85 to-transparent text-left text-sm text-background-50">
              {img.caption}
            </span>
            <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/85 text-primary-700 opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-fullscreen-line"></i>
            </span>
          </button>
        ))}
      </div>

      {active !== null && (
        <div
          className="fixed inset-0 z-[60] bg-primary-950/90 flex items-center justify-center px-4 py-10 animate-slide-fade"
          onClick={close}
        >
          <button
            type="button"
            aria-label="关闭预览"
            onClick={close}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            type="button"
            aria-label="上一张"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 md:left-8 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-h-[72vh] rounded-lg overflow-hidden bg-background-900">
              <img
                src={images[active].src}
                alt={images[active].caption}
                title={`${images[active].caption} - 森泽佳奈`}
                className="w-full h-full object-contain"
              />
            </div>
            <figcaption className="text-center text-background-100 text-sm mt-4">
              {images[active].caption}（{active + 1} / {images.length}）
            </figcaption>
          </figure>

          <button
            type="button"
            aria-label="下一张"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 md:right-8 w-11 h-11 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      )}
    </>
  );
}